import React, { createContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconUsers, IconClockHour4, IconBan } from "@tabler/icons-react";
import { ApiConstant, AppConstant, LangConstant } from "const";
import { PageTitle } from "components";
import { useDeleteUser, useImportUser, useUserList, useRole } from "hooks";
import { SystemUtils } from "utils";
import {
  ConfirmActiveDialog,
  GeneratedPassword,
  Heading,
  ImportUserFailureDialog,
  ImportUserFromExcelDialog,
  ImportUserSuccessDialog,
  UserAction,
  UserInfoDialog,
  UserListTable,
  DeleteUserDialog,
} from "./components";
import UserRequestManagement from "pages/UserRequestManagement";
import { BRANCH_REQUEST_STATUS, TAB_USER_MANAGEMENT_VALUE } from "const/system.const";

export const UserManagementContext = createContext({});

const UserManagement = () => {
  const { t: getLabel } = useTranslation([LangConstant.NS_COMMON, LangConstant.NS_LOGIN]);

  const { isServiceAdmin } = useRole();

  const {
    form,
    activeFunctionOptions,
    currentPageUsers,
    pagination,
    isLoading,
    handleChangeActiveFunction,
    handleChangeSearchInput,
    handleChangePage,
    handleChangeUnitDropdown,
    handleChangeDepartmentDropdown,
    handleChangePositionDropdown,
    funcExportExcel,
  } = useUserList();

  const { importUsersAsync } = useImportUser();
  const { deleteUser } = useDeleteUser();

  const [selectedUser, setSelectedUser] = useState({ data: null, isOpen: false, isEdit: false });
  const [newPassword, setNewPassword] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [checkedUsers, setCheckedUsers] = useState({
    data: [],
    isDisable: false,
    isOpenDialog: false,
  });
  const [importUserDialogConfig, setImportUserDialogConfig] = useState({ open: false });
  const [importUserStatusConfig, setImportUserStatusConfig] = useState({ open: false, status: null, fileLink: null });

  const [tabUser, setTabUser] = useState(TAB_USER_MANAGEMENT_VALUE.userList);

  const [isDeleteUserDialog, setIsDeleteUserDialog] = useState(false);

  const handleOpenAction = (event, user) => {
    setAnchorEl(event.target);
    setSelectedUser(state => ({ ...state, data: user }));
  };

  const handleCloseAction = () => {
    setAnchorEl(null);
  };

  const handleCloseUserInfoDialog = () => {
    setSelectedUser(state => ({ ...state, data: null, isOpen: false, isEdit: false }));
  };

  const handleOpenGeneratedPasswordDialog = password => {
    setNewPassword(password);
  };

  const handleCloseGeneratedPasswordDialog = () => {
    setNewPassword(null);
    setSelectedUser(state => ({ ...state, data: null, isOpen: false, isEdit: false }));
  };

  const handleOpenUserInfo = () => {
    setAnchorEl(null);
    setSelectedUser(state => ({ ...state, isOpen: true, isEdit: false }));
  };

  const handleOpenEditUserInfo = () => {
    setAnchorEl(null);
    setSelectedUser(state => ({ ...state, isOpen: true, isEdit: true }));
  };

  const handleOpenCreateUser = () => {
    setSelectedUser(state => ({ ...state, data: null, isOpen: true, isEdit: true }));
  };

  const handleCheckboxChange = (isChecked, user) => {
    if (isChecked) {
      setCheckedUsers(state => ({ ...state, data: [...state.data, user] }));
    } else {
      setCheckedUsers(state => ({ ...state, data: state.data.filter(checkedUser => checkedUser.id !== user.id) }));
    }
  };

  const handleCheckAllChange = isChecked => {
    if (isChecked) {
      setCheckedUsers(state => {
        const existingIds = new Set(state.data.map(u => u.id));
        const newUsersToAdd = currentPageUsers.filter(u => !existingIds.has(u.id));
        return { ...state, data: [...state.data, ...newUsersToAdd] };
      });
    } else {
      setCheckedUsers(state => {
        const currentPageUserIds = new Set(currentPageUsers.map(u => u.id));
        return { ...state, data: state.data.filter(u => !currentPageUserIds.has(u.id)) };
      });
    }
  };

  const handleClickActiveButton = () => {
    setCheckedUsers(state => ({ ...state, isOpenDialog: true, isDisable: false }));
  };

  const handleClickInactiveButton = () => {
    setCheckedUsers(state => ({ ...state, isOpenDialog: true, isDisable: true }));
  };

  const handleClickDeleteButton = () => {
    setIsDeleteUserDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteUserDialog(false);
  };

  const handleCloseConfirmActiveDialog = () => {
    setCheckedUsers(state => ({ ...state, isOpenDialog: false }));
  };

  const handleClearCheckedUser = () => {
    setCheckedUsers(state => ({ ...state, data: [] }));
  };

  const handleCloseImportUserDialog = () => {
    setImportUserDialogConfig({ open: false });
  };

  const handleDownloadFile = () => {
    const timestamp = new Date().getTime();
    const link = document.createElement("a");
    link.setAttribute("href", window.env.URL_TEMPLATE);
    link.setAttribute("download", `Template_trios_import_account_2024_v1_${timestamp}.xlsx`);
    link.click();
    link.remove();
  };

  const handleSubmitFile = async file => {
    try {
      const maxFileNameLength = 255;
      const maxFileSize = 25 * 1024 * 1024; // 25 MB
      const acceptableFileExt = "xlsx";
      const fileArray = file.name.split(".");
      const fileExt = fileArray[fileArray.length - 1];
      if (
        file.name.length > maxFileNameLength ||
        file.size > maxFileSize ||
        fileExt !== acceptableFileExt ||
        !AppConstant.IMPORT_USER_FILE_EXTENSIONS.includes(file.type)
      ) {
        setImportUserStatusConfig(state => ({ ...state, open: true, status: "error", fileLink: null }));
      } else {
        const response = await importUsersAsync(file);
        if (response.status === ApiConstant.STT_OK) {
          handleCloseImportUserDialog();
          setImportUserStatusConfig(state => ({ ...state, open: true, status: "success", fileLink: null }));
        } else if (response.status === ApiConstant.STT_BAD_REQUEST && response.data.type === "application/json") {
          setImportUserStatusConfig(state => ({ ...state, open: true, status: "error", fileLink: null }));
        } else if (response.status === ApiConstant.STT_BAD_REQUEST) {
          const url = URL.createObjectURL(response.data);
          setImportUserStatusConfig(state => ({ ...state, open: true, status: "error", fileLink: url }));
        } else {
          SystemUtils.appendNotification(getLabel(LangConstant.TXT_UPDATE_ERROR), "error");
        }
      }
    } catch (error) {
      console.error(error);
      SystemUtils.appendNotification(getLabel(LangConstant.TXT_UPDATE_ERROR), "error");
    }
  };

  const handleCloseImportUserStatusDialog = () => {
    setImportUserStatusConfig({ open: false, status: null, fileLink: null });
  };

  const handleClickDownloadErrorFile = () => {
    const timestamp = new Date().getTime();
    const link = document.createElement("a");
    link.setAttribute("href", importUserStatusConfig.fileLink);
    link.setAttribute("download", `Trios_import_account_${timestamp}.xlsx`);
    link.click();
    link.remove();
    handleCloseImportUserStatusDialog();
    handleCloseImportUserDialog();
  };

  const handleOpenImportDialog = () => {
    setImportUserDialogConfig({ open: true });
  };

  const handleDeleteUsers = () => {
    deleteUser({
      users: checkedUsers.data,
      onClose: () => {
        handleClearCheckedUser();
        handleCloseDeleteDialog();
      },
    });
  };

  const onClickUserList = () => {
    setTabUser(TAB_USER_MANAGEMENT_VALUE.userList);
  };

  const onClickUserRequest = () => {
    setTabUser(TAB_USER_MANAGEMENT_VALUE.userRequest);
  };

  const onClickUserReject = () => {
    setTabUser(TAB_USER_MANAGEMENT_VALUE.userReject);
  };

  const titleObjectContent = getLabel(LangConstant.OBJ_SIDEBAR_TITLE, { returnObjects: true });

  const contextValue = {};

  return (
    <UserManagementContext.Provider value={contextValue}>
      <PageTitle>{titleObjectContent.userManagement}</PageTitle>

      <div className="card">
        {/* Tabs in card header */}
        {!isServiceAdmin && (
          <div className="card-header">
            <ul className="nav nav-tabs card-header-tabs">
              <li className="nav-item">
                <button
                  className={`nav-link ${tabUser === TAB_USER_MANAGEMENT_VALUE.userList ? "active" : ""}`}
                  onClick={onClickUserList}
                >
                  <IconUsers size={16} className="me-1" />
                  {getLabel(LangConstant.TXT_TAB_USER_LIST)}
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${tabUser === TAB_USER_MANAGEMENT_VALUE.userRequest ? "active" : ""}`}
                  onClick={onClickUserRequest}
                >
                  <IconClockHour4 size={16} className="me-1" />
                  {getLabel(LangConstant.TXT_TAB_PENDING_LIST)}
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${tabUser === TAB_USER_MANAGEMENT_VALUE.userReject ? "active" : ""}`}
                  onClick={onClickUserReject}
                >
                  <IconBan size={16} className="me-1" />
                  {getLabel(LangConstant.TXT_TAB_REJECTED_LIST)}
                </button>
              </li>
            </ul>
          </div>
        )}

        <div className="card-body">
          {tabUser === TAB_USER_MANAGEMENT_VALUE.userList && (
            <>
              <Heading
                form={form}
                activeFunctionOptions={activeFunctionOptions}
                checkedUsers={checkedUsers}
                pagination={pagination}
                funcExportExcel={funcExportExcel}
                onChangeSearchInput={handleChangeSearchInput}
                onChangeActiveFunction={handleChangeActiveFunction}
                onClickActiveButton={handleClickActiveButton}
                onClickInactiveButton={handleClickInactiveButton}
                onClickImportUser={handleOpenImportDialog}
                onClickCreateUser={handleOpenCreateUser}
                onClickDeleteButton={handleClickDeleteButton}
                handleChangeUnitDropdown={handleChangeUnitDropdown}
                handleChangeDepartmentDropdown={handleChangeDepartmentDropdown}
                handleChangePositionDropdown={handleChangePositionDropdown}
                onClearAllChecked={handleClearCheckedUser}
              />
              <UserListTable
                isLoading={isLoading}
                users={currentPageUsers}
                pagination={pagination}
                checkedUsers={checkedUsers.data}
                onClickAction={handleOpenAction}
                onChangePage={handleChangePage}
                onCheckBoxChange={handleCheckboxChange}
                onCheckAllChange={handleCheckAllChange}
                onClearAllChecked={handleClearCheckedUser}
              />
              <UserAction
                data={selectedUser.data}
                anchorEl={anchorEl}
                open={Boolean(anchorEl && selectedUser.data)}
                onCloseAction={handleCloseAction}
                onClickUserInfo={handleOpenUserInfo}
                onClickUserEdit={handleOpenEditUserInfo}
                onOpenPasswordDialog={handleOpenGeneratedPasswordDialog}
              />
              {selectedUser.isOpen && (
                <UserInfoDialog
                  data={selectedUser.data}
                  isOpen={true}
                  isEdit={selectedUser.isEdit}
                  onClose={handleCloseUserInfoDialog}
                />
              )}
              <GeneratedPassword
                isOpen={Boolean(newPassword)}
                password={newPassword}
                onClose={handleCloseGeneratedPasswordDialog}
              />
              <ConfirmActiveDialog
                users={checkedUsers.data}
                isDisable={checkedUsers.isDisable}
                isShow={checkedUsers.isOpenDialog}
                onClose={handleCloseConfirmActiveDialog}
                onClear={handleClearCheckedUser}
              />
              <ImportUserFromExcelDialog
                open={Boolean(importUserDialogConfig.open)}
                onClose={handleCloseImportUserDialog}
                onDownload={handleDownloadFile}
                onSubmit={handleSubmitFile}
              />
              <ImportUserSuccessDialog
                open={Boolean(importUserStatusConfig.open && importUserStatusConfig.status === "success")}
                onClose={handleCloseImportUserStatusDialog}
              />
              <ImportUserFailureDialog
                open={Boolean(importUserStatusConfig.open && importUserStatusConfig.status === "error")}
                isFailureValidation={!Boolean(importUserStatusConfig.fileLink)}
                onClose={handleCloseImportUserStatusDialog}
                onClickDownload={handleClickDownloadErrorFile}
              />
              <DeleteUserDialog
                open={isDeleteUserDialog}
                onClose={handleCloseDeleteDialog}
                onConfirm={handleDeleteUsers}
              />
            </>
          )}
          {tabUser === TAB_USER_MANAGEMENT_VALUE.userRequest && (
            <UserRequestManagement status={BRANCH_REQUEST_STATUS.PENDING.status} />
          )}
          {tabUser === TAB_USER_MANAGEMENT_VALUE.userReject && (
            <UserRequestManagement status={BRANCH_REQUEST_STATUS.REJECTED.status} />
          )}
        </div>
      </div>
    </UserManagementContext.Provider>
  );
};

export default UserManagement;
